import React, {useCallback, useEffect, useRef, useState} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {Form, FormInput, FormActionsContext} from '@modifi/plugin-forms'
import {
  Row,
  Col,
  withInputContainer,
  DatePickerPure,
  CustomerDialogButtonRow,
  DangerAlert,
  InfoAlert,
} from '@modifi/ui'
import {seaNavy} from '@modifi/colors'
import {useTranslation} from '@modifi/plugin-i18n'

import ShipIcon from '../../../../ui/ship-icon'
import DocumentProcessingIcon from '../../../../ui/document-processing-icon'
import DocumentUploadingIcon from '../../../../ui/document-uploading-icon'
import {Wrapper, IconWrapper, DescriptionWrapper, PositionedSuccessBadge} from './styles'
import {getCurrentSubmitProgress} from './utils'
import {
  INPUT_CONTAINER_ID,
  INPUT_SHIPMENT_START_DATE,
} from '../../components/submit-shipment/constants'
import {validateContainerId} from '../../components/submit-shipment/utils'
import DotLoader from '../../../../ui/dot-loader'
import {FormInputCapitalized} from '../../../../components'

const DatePickerPureWithContainer = withInputContainer(DatePickerPure)

const propTypes = {
  params: PropTypes.shape({
    containerId: PropTypes.string,
    shipmentStartDate: PropTypes.string,
    fileIdent: PropTypes.string,
    fileId: PropTypes.string,
    documentType: PropTypes.string,
    extractedContainerIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  }).isRequired,
  fileUploadProgress: PropTypes.number,
  submitPending: PropTypes.bool.isRequired,
  submitHasError: PropTypes.bool.isRequired,
  submitErrorMessage: PropTypes.string,
  submitShipment: PropTypes.func.isRequired,
  clearShipment: PropTypes.func.isRequired,
  closeDialog: PropTypes.func.isRequired,
  errorObject: PropTypes.shape({
    errorMessage: PropTypes.string,
    errorCode: PropTypes.number,
  }).isRequired,
}
const defaultProps = {
  fileUploadProgress: undefined,
  submitErrorMessage: undefined,
}

export type SubmitProgressDialogProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const SubmitProgressDialog: React.FC<SubmitProgressDialogProps> = ({
  fileUploadProgress,
  submitPending,
  submitErrorMessage,
  submitHasError,
  params,
  submitShipment,
  closeDialog,
  clearShipment,
  errorObject,
}) => {
  const [submitDone, setSubmitDone] = useState(false)
  const {containerId, shipmentStartDate, fileIdent, fileId, extractedContainerIds, documentType} =
    params

  const {t} = useTranslation(['argus', 'plugin-forms'], {useSuspense: false})

  const currentState = getCurrentSubmitProgress(
    !!fileIdent || !!fileId,
    submitPending,
    submitDone,
    fileUploadProgress
  )

  useEffect(() => {
    clearShipment()
  }, [clearShipment])

  // container id provided manually
  useEffect(() => {
    if (containerId && shipmentStartDate) {
      submitShipment({containerId, shipmentStartDate})
      return undefined
    }
    return () => {
      clearShipment()
    }
  }, [containerId, shipmentStartDate, submitShipment, clearShipment])

  const prevFileId = useRef(fileId)

  const prevSubmitPending = useRef(submitPending)
  useEffect(() => {
    if (prevSubmitPending.current !== submitPending) {
      prevSubmitPending.current = submitPending

      if (!submitPending) {
        if (submitHasError && errorObject.errorCode !== 409 && containerId && shipmentStartDate) {
          closeDialog()
        } else if (!submitHasError) {
          setSubmitDone(true)
        }
      }
    }
  }, [submitPending, closeDialog, submitHasError, containerId, shipmentStartDate, errorObject])

  // calling submit directly when both shipmentStartDate and containerIds are extracted from the doc
  useEffect(() => {
    if (
      !prevFileId.current &&
      !!fileId &&
      extractedContainerIds &&
      extractedContainerIds.length > 0 &&
      !!shipmentStartDate
    ) {
      prevFileId.current = fileId
      submitShipment({
        shipmentStartDate,
        extractedContainerIds,
        documentRef: {
          fileId,
          documentType,
        },
      })
    }
  }, [extractedContainerIds, shipmentStartDate, submitShipment, documentType, fileId])

  const handleFormSubmit = useCallback(
    (data: Record<string, any>) => {
      submitShipment({
        containerId: data.containerId,
        shipmentStartDate: shipmentStartDate || data.shipmentStartDate,
        extractedContainerIds,
        documentRef: {
          fileId,
          documentType,
        },
      })
    },
    [documentType, extractedContainerIds, fileId, shipmentStartDate, submitShipment]
  )

  switch (currentState) {
    case 'UPLOADING':
      return (
        <Wrapper>
          <Row alignItems="center">
            <Col col={12}>
              <IconWrapper>
                <DocumentUploadingIcon height={60} width={60} color={seaNavy} />
              </IconWrapper>
            </Col>
            <Col col={12} mt={16}>
              <DescriptionWrapper>
                {t('submit-progress-dialog.uploading.description', {fileUploadProgress})}
              </DescriptionWrapper>
            </Col>
          </Row>
        </Wrapper>
      )
    case 'PROCESSING':
      return (
        <Wrapper>
          <Row alignItems="center">
            <Col col={12}>
              <IconWrapper>
                <DocumentProcessingIcon height={60} width={60} color={seaNavy} />
              </IconWrapper>
            </Col>
            <Col col={12} mt={16}>
              <DescriptionWrapper>
                {t('submit-progress-dialog.processing.description')}
                <DotLoader />
              </DescriptionWrapper>
            </Col>
          </Row>
        </Wrapper>
      )
    case 'UPLOAD_DONE':
      return (
        <Form onSubmit={handleFormSubmit} onDiscard={closeDialog}>
          <Row mb={16}>
            <Col col={12}>
              {submitErrorMessage ? (
                <DangerAlert>{submitErrorMessage}</DangerAlert>
              ) : (
                <InfoAlert>{t('submit-progress-dialog.extraction-incomplete.alert')}</InfoAlert>
              )}
            </Col>
          </Row>
          {!extractedContainerIds?.length && (
            <Row>
              <Col col={12}>
                <FormInputCapitalized
                  name={INPUT_CONTAINER_ID}
                  label={t('submit-shipment-form.container-id.label')}
                  required
                  disabled={submitPending}
                  validationFunction={validateContainerId}
                />
              </Col>
            </Row>
          )}
          {!shipmentStartDate && (
            <Row>
              <Col col={12}>
                <FormInput
                  inputComponent={DatePickerPureWithContainer}
                  name={INPUT_SHIPMENT_START_DATE}
                  fromDate={false}
                  t={t}
                  label={t('submit-shipment-form.shipment-start-date.label')}
                  disabled={submitPending}
                  required
                />
              </Col>
            </Row>
          )}
          <FormActionsContext.Consumer>
            {({canSubmit, submit, discard}) => (
              <CustomerDialogButtonRow
                hasCancel
                canCancel
                cancelText={t('cta.cancel')}
                onCancelClick={discard}
                canSubmit={canSubmit}
                submitText={t('cta.continue')}
                onSubmitClick={submit}
              />
            )}
          </FormActionsContext.Consumer>
        </Form>
      )
    case 'SUBMITTING':
      return (
        <Wrapper>
          <Row>
            <Col col={12}>
              <IconWrapper>
                <ShipIcon height={60} width={200} color={seaNavy} />
              </IconWrapper>
            </Col>
            <Col col={12} mt={16}>
              <DescriptionWrapper>
                {t('submit-progress-dialog.submitting.description')}
                <DotLoader />
              </DescriptionWrapper>
            </Col>
          </Row>
        </Wrapper>
      )
    case 'SUBMITTED':
      return (
        <Wrapper>
          <Row>
            <Col col={12}>
              <IconWrapper>
                <ShipIcon height={60} width={200} color={seaNavy} />
                <PositionedSuccessBadge />
              </IconWrapper>
            </Col>
            <Col col={12} mt={16}>
              <DescriptionWrapper>
                {t('submit-progress-dialog.submitted.description')}
              </DescriptionWrapper>
            </Col>
          </Row>
          <CustomerDialogButtonRow
            modal
            onSubmitClick={closeDialog}
            submitText={t('cta.close')}
            autoCloseDuration={5}
          />
        </Wrapper>
      )
    default:
      return null
  }
}

SubmitProgressDialog.propTypes = propTypes
SubmitProgressDialog.defaultProps = defaultProps

export default SubmitProgressDialog
